import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Typography, Avatar, Box, Divider, Card, Button, CircularProgress } from "@mui/material";
import Navigation from "../../Reusbale/Navigation";
import Footer from "../../Reusbale/Footer";
import COLORS from "../../../../../constants/colors";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import baseURL from "../../config";

// Helper function to determine if a URL points to an image
const isImage = (url) => {
    if (!url) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const extension = url.substring(url.lastIndexOf('.')).toLowerCase();
    return imageExtensions.includes(extension);
};

const OrganizationDetailsPage = () => {
    const { slug } = useParams();
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    useEffect(() => {
        const fetchOrgData = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${baseURL}/admin/organization/${slug}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                });

                const data = await response.json();

                if (data.organization) {
                    setOrganization(data.organization);
                } else {
                    setOrganization(null);
                }
            } catch (error) {
                console.error("Error fetching organization data:", error);
                toast.error("Failed to fetch organization data.");
                setOrganization(null);
            } finally {
                setLoading(false);
            }
        };

        fetchOrgData();
    }, [slug]);

    const handleApproveOrg = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${baseURL}/admin/approve-organization/${slug}`, {
                method: "PUT",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const data = await response.json();

            if (response.ok) {
                toast.success("Organization approved successfully!");
                setOrganization(prev => ({ ...prev, verified: true }));
            } else {
                toast.error(data.message || "Failed to approve organization.");
            }
        } catch (error) {
            console.error("Approval error:", error);
            toast.error("Failed to approve organization. Please try again.");
        }
        setLoading(false);
    };

    const handleDeleteOrg = async () => {
        if (window.confirm("Are you sure you want to delete this organization? This action is irreversible!")) {
            try {
                const response = await fetch(`${baseURL}/admin/delete-organization/${slug}`, {
                    method: "DELETE",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                });

                const data = await response.json();

                if (response.ok) {
                    toast.success('Organization deleted successfully');
                    setTimeout(() => {
                        navigate(-1);
                    }, 1000);
                } else {
                    toast.error(data.message || "Failed to delete organization");
                }
            } catch (error) {
                toast.error(error.message || "An unexpected error occurred");
            }
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
                <CircularProgress size={24} />
                <Typography sx={{ mt: 2, fontSize: "0.8rem", color: "text.secondary" }}>
                    Loading organization data... Please wait
                </Typography>
            </Box>
        );
    }

    if (!organization) {
        return (
            <Typography variant="h6" color="error" sx={{ textAlign: "center", mt: 4, fontSize: '1rem' }}>
                Organization not found
            </Typography>
        );
    }

    const hasIds = organization.org_ids && organization.org_ids.length > 0 && organization.org_ids[0].id;
    const firstId = hasIds ? organization.org_ids[0] : null;

    // A reusable component to render document previews
    const DocumentPreview = ({ label, url }) => {
        if (!url) {
            return <Typography sx={{ fontSize: "0.8rem", color: "gray", flex: 1 }}>No {label} provided.</Typography>;
        }

        const isImageFile = isImage(url);
        
        return (
            <Box sx={{ textAlign: "center", flex: 1 }}>
                {isImageFile ? (
                    <img
                        src={url}
                        alt={label}
                        style={{ width: "100%", maxWidth: "300px", height: "auto", objectFit: "contain", borderRadius: "8px", border: '1px solid #ccc' }}
                    />
                ) : (
                    <Button variant="outlined" component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none' }}>
                        View {label}
                    </Button>
                )}
                <Typography sx={{ fontSize: "0.8rem", mt: 1 }}>{label}</Typography>
            </Box>
        );
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#EEF2F7" }}>
            <Navigation />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 2,
                    minHeight: "100vh",
                    marginLeft: { xs: 0, md: 35 },
                    overflow: "hidden",
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        color: "#666",
                        mb: 2,
                        fontWeight: "600",
                        fontSize: "1.1rem",
                        mt: { xs: 7, md: 0 },
                    }}
                >
                    Organization Details
                </Typography>

                <Card
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "center", sm: "center" },
                        gap: 3,
                        backgroundColor: "#fff",
                        borderRadius: "16px",
                        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                        border: "1px solid rgba(0,0,0,0.05)",
                        p: 3,
                        mb: 3,
                        textAlign: { xs: "center", sm: "left" },
                    }}
                >
                    <Avatar
                        src={organization.logo_url}
                        alt={organization.name}
                        sx={{
                            width: 100,
                            height: 100,
                            border: `1px solid ${COLORS.primary}`,
                            mb: { xs: 2, sm: 0 },
                        }}
                    />

                    <Box sx={{ flexGrow: 1 }}>
                        <Typography
                            variant="h5"
                            fontWeight="bold"
                            color={COLORS.primary}
                            sx={{ fontSize: "1.2rem" }}
                        >
                            {organization.name}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ fontSize: "0.8rem" }}
                        >
                            {organization.email}
                        </Typography>
                    </Box>

                    {organization.verified === 0 ? (
                        <Button
                            variant="contained"
                            color="success"
                            size="medium"
                            sx={{
                                textTransform: 'none',
                                minWidth: '140px',
                                mt: { xs: 2, sm: 0 },
                            }}
                            startIcon={!loading && <CheckCircleIcon />}
                            onClick={handleApproveOrg}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={18} color="inherit" sx={{ marginRight: 1 }} />
                                    Please wait...
                                </>
                            ) : (
                                "Approve"
                            )}
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            color="success"
                            sx={{
                                textTransform: 'none',
                                mt: { xs: 2, sm: 0 },
                            }}
                            size="medium"
                            startIcon={<CheckCircleIcon />}
                            disabled
                        >
                            Approved
                        </Button>
                    )}
                </Card>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                    {/* Financial Summary */}
                    <Card sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, flex: 1, mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "0.95rem", letterSpacing: "-0.2px", mb: 1 }}>Financial Summary</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography sx={{ fontSize: "0.8rem" }}><strong>Total Raised:</strong> UGX {organization.raised ? organization.raised.toLocaleString() : "0"}</Typography>
                        <Typography sx={{ fontSize: "0.8rem" }}><strong>Total Withdrawn:</strong> UGX {organization.total_withdrawn ? organization.total_withdrawn.toLocaleString() : "0"}</Typography>
                        <Typography sx={{ fontSize: "0.8rem" }}><strong>Goal:</strong> UGX {organization.goal ? organization.goal.toLocaleString() : "0"}</Typography>
                        <Typography sx={{ fontSize: "0.8rem" }}><strong>Balance:</strong> UGX {organization.balance ? organization.balance.toLocaleString() : "0"}</Typography>
                    </Card>
                    
                    {/* Account Information */}
                    <Card sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, flex: 1, mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "0.95rem", letterSpacing: "-0.2px", mb: 1 }}>Organization Information</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography sx={{ fontSize: "0.8rem" }}><strong>Phone:</strong> {organization.phone || "N/A"}</Typography>
                        <Typography sx={{ fontSize: "0.8rem" }}><strong>Email:</strong> {organization.email || "N/A"}</Typography>
                        <Typography sx={{ fontSize: "0.8rem" }}><strong>Website:</strong> {organization.website || "N/A"}</Typography>
                        <Typography sx={{ fontSize: "0.8rem" }}><strong>Physical Address:</strong> {organization.physical_address || "N/A"}</Typography>
                        <Typography sx={{ fontSize: "0.8rem" }}><strong>Type:</strong> {organization.orgType || "N/A"}</Typography>
                        <Typography sx={{ fontSize: "0.8rem" }}><strong>Joined:</strong> {formatDate(organization.created_at)}</Typography>
                        <Typography sx={{ fontSize: "0.8rem" }}><strong>Verified:</strong> {organization.verified ? "Yes" : "No"}</Typography>
                    </Card>
                </Box>

                {/* Campaigns Section */}
<Card sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, mb: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "0.95rem", letterSpacing: "-0.2px", mb: 1 }}>
        Campaigns
    </Typography>
    <Divider sx={{ mb: 2 }} />
    {organization.campaigns && organization.campaigns.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {organization.campaigns.map((campaign) => (
                <Card
                    key={campaign.id}
                    sx={{
                        p: 2,
                        borderRadius: "12px",
                        border: "1px solid rgba(0,0,0,0.05)",
                        backgroundColor: "#fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    }}
                >
                    <Box sx={{ display: "flex", gap: 2 }}>
                        {campaign.image_url && (
                            <img
                                src={campaign.image_url}
                                alt={campaign.name}
                                style={{
                                    width: "120px",
                                    height: "90px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    border: "1px solid #ccc",
                                }}
                            />
                        )}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: "0.95rem" }}>
                                {campaign.name}
                            </Typography>
                            <Typography sx={{ fontSize: "0.8rem", color: "gray", mb: 1 }}>
                                Status:{" "}
                                <span
                                    style={{
                                        color:
                                            campaign.status === "open"
                                                ? "green"
                                                : campaign.status === "closed"
                                                ? "red"
                                                : "orange",
                                    }}
                                >
                                    {campaign.status.toUpperCase()}
                                </span>
                            </Typography>
                            <Typography sx={{ fontSize: "0.8rem" }}>
                                Raised: UGX {campaign.raised?.toLocaleString()} / UGX {campaign.goal?.toLocaleString()}
                            </Typography>
                            <Typography sx={{ fontSize: "0.8rem" }}>
                                Donations: {campaign.donations}
                            </Typography>
                            <Typography sx={{ fontSize: "0.8rem" }}>
                                Created: {formatDate(campaign.created_at)}
                            </Typography>
                        </Box>
                    </Box>
                </Card>
            ))}
        </Box>
    ) : (
        <Typography sx={{ fontSize: "0.8rem", color: "gray", textAlign: "center" }}>
            No campaigns found for this organization.
        </Typography>
    )}
</Card>


                {/* National ID Photos Section */}
                <Card sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "0.95rem", letterSpacing: "-0.2px", mb: 1 }}>National ID Photos</Typography>
                    <Divider sx={{ mb: 2 }} />
                    {hasIds ? (
                        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 3, justifyContent: "center" }}>
                            <DocumentPreview label="Front ID" url={firstId.national_id_front_url} />
                            <DocumentPreview label="Back ID" url={firstId.national_id_back_url} />
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: "0.8rem", color: "gray", textAlign: 'center' }}>No National ID photos found for this organization.</Typography>
                    )}
                </Card>
                
                {/* KYC Documents Section */}
                <Card sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "0.95rem", letterSpacing: "-0.2px", mb: 1 }}>KYC Documents</Typography>
                    <Divider sx={{ mb: 2 }} />
                    {organization.kyc_documents && organization.kyc_documents.status ? (
                        <Box>
                            <Box sx={{ mb: 2 }}>

                            </Box>
                            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 3, justifyContent: "center" }}>
                                <DocumentPreview label="CSO Certificate" url={organization.kyc_documents.cso_certificate_url} />
                                <DocumentPreview label="NGO Certificate" url={organization.kyc_documents.ngo_certificate_url} />
                                <DocumentPreview label="NGO Permit" url={organization.kyc_documents.ngo_permit_url} />
                            </Box>
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: "0.8rem", color: "gray", textAlign: 'center' }}>No KYC documents found for this organization.</Typography>
                    )}
                </Card>


                {/* Delete Account Section */}
                <Card sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, mb: 3, mt: 3, textAlign: "left" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "0.95rem", letterSpacing: "-0.2px", mb: 1 }}>
                        Account Actions
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography sx={{ fontSize: "0.9rem", color: "gray", mb: 2 }}>
                        Deleting this organization's account will permanently remove the organization and all its associated data, including financial records. This action cannot be undone.
                    </Typography>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteOrg}
                        sx={{
                            textTransform: "none",
                        }}
                    >
                        Delete Organization
                    </Button>
                </Card>

                <Footer />
            </Box>
        </Box>
    );
};

export default OrganizationDetailsPage;